import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Divider,
  Drawer,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import Typography from "components/basic/typography";
import { EVENT_TYPE_MAP } from "../../../calendar/eventTypes";
import {
  MEETING_STATUS_META,
  statusFilterLabel,
  MEETING_TYPES,
  StatusCounts,
  StatusFilter,
  TypeFilter,
} from "../../types";

interface MeetingsSidebarProps {
  statusFilter: StatusFilter;
  onStatusFilter: (s: StatusFilter) => void;
  typeFilter: TypeFilter;
  onTypeFilter: (t: TypeFilter) => void;
  statusCounts: StatusCounts;
  typeCounts: Record<string, number>;
  /** Below md the rail is a drawer, opened from the toolbar's Filters button. */
  open: boolean;
  onClose: () => void;
}

const STATUS_ITEMS: { key: StatusFilter; color?: string }[] = [
  { key: "all" },
  { key: "pending", color: MEETING_STATUS_META.pending.color },
  {
    key: "awaiting_customer",
    color: MEETING_STATUS_META.awaiting_customer.color,
  },
  { key: "confirmed", color: MEETING_STATUS_META.confirmed.color },
  { key: "completed", color: MEETING_STATUS_META.completed.color },
  { key: "cancelled", color: MEETING_STATUS_META.cancelled.color },
  {
    key: "reschedule_requested",
    color: MEETING_STATUS_META.reschedule_requested.color,
  },
  { key: "declined", color: MEETING_STATUS_META.declined.color },
];

const NavRow: React.FC<{
  active: boolean;
  icon: React.ReactNode;
  label: string;
  count: number;
  accent: string;
  onClick: () => void;
}> = ({ active, icon, label, count, accent, onClick }) => {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      onClick={onClick}
      sx={{
        px: 1,
        py: 0.75,
        borderRadius: 1,
        cursor: "pointer",
        borderLeft: `3px solid ${active ? accent : "transparent"}`,
        backgroundColor: active ? `${accent}14` : "transparent",
        "&:hover": { backgroundColor: theme.palette.action.hover },
      }}
    >
      <Box sx={{ color: accent, display: "flex" }}>{icon}</Box>
      <Typography
        variant="body2"
        style={{ flex: 1, fontWeight: active ? 600 : 400 }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          minWidth: 22,
          textAlign: "center",
          px: 0.75,
          borderRadius: "10px",
          fontSize: "0.72rem",
          fontWeight: 600,
          color: theme.palette.text.secondary,
          backgroundColor: theme.palette.action.selected,
        }}
      >
        {count}
      </Box>
    </Stack>
  );
};

const MeetingsSidebar: React.FC<MeetingsSidebarProps> = ({
  statusFilter,
  onStatusFilter,
  typeFilter,
  onTypeFilter,
  statusCounts,
  typeCounts,
  open,
  onClose,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("meetings");
  // 230px of filter rail on top of the dashboard's own chrome leaves nothing
  // for the table, so below md it moves out of the flow into a drawer.
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));

  const rail = (
    <Box
      sx={{
        width: { xs: "min(280px, 85vw)", md: 230 },
        flexShrink: 0,
        height: "100%",
        borderRight: { md: `1px solid ${theme.palette.divider}` },
        p: 1.5,
        overflowY: "auto",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <CalendarMonthIcon fontSize="small" color="warning" />
        <Typography variant="h4" style={{ fontWeight: 600 }}>
          {t("sidebar.title")}
        </Typography>
      </Stack>

      <Stack spacing={0.25}>
        {STATUS_ITEMS.map((s) => (
          <NavRow
            key={s.key}
            active={statusFilter === s.key}
            icon={
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: s.color ?? theme.palette.text.secondary,
                }}
              />
            }
            label={statusFilterLabel(s.key)}
            count={statusCounts[s.key]}
            accent={s.color ?? theme.palette.warning.main}
            onClick={() => {
              onStatusFilter(s.key);
              onClose();
            }}
          />
        ))}
      </Stack>

      <Divider sx={{ my: 1.5 }} />

      <Typography
        variant="caption"
        style={{
          color: theme.palette.text.secondary,
          fontWeight: 600,
          paddingLeft: 8,
        }}
      >
        {t("sidebar.types")}
      </Typography>
      <Stack spacing={0.25} sx={{ mt: 0.5 }}>
        {MEETING_TYPES.map((type) => {
          const meta = EVENT_TYPE_MAP[type];
          const Icon = meta.icon;
          const active = typeFilter === type;
          return (
            <NavRow
              key={type}
              active={active}
              icon={<Icon sx={{ fontSize: 18, color: meta.color }} />}
              label={meta.label}
              count={typeCounts[type] ?? 0}
              accent={meta.color}
              // Toggle off when re-clicking the active type.
              onClick={() => {
                onTypeFilter(active ? "all" : type);
                onClose();
              }}
            />
          );
        })}
      </Stack>
    </Box>
  );

  if (isCompact) {
    return (
      <Drawer
        open={open}
        onClose={onClose}
        // The app-wide drawer paper is dark; these filters are a light panel.
        PaperProps={{
          sx: {
            width: "auto",
            background: theme.palette.background.paper,
            color: theme.palette.text.primary,
          },
        }}
      >
        {rail}
      </Drawer>
    );
  }

  return rail;
};

export default MeetingsSidebar;
